/**
 * Lyrics Prefetcher - Pre-fetches lyrics in the background for instant display
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Global cache for prefetched lyrics
const prefetchCache = new Map();

/**
 * Pre-fetch lyrics for a song in the background
 * @param {string} title - Song title
 * @param {string} artist - Artist name
 */
export const prefetchLyrics = async (title, artist) => {
  if (!title || !artist) return;
  
  const cacheKey = `${title.toLowerCase()}-${artist.toLowerCase()}`;
  
  // Skip if already cached
  if (prefetchCache.has(cacheKey)) {
    return;
  }
  
  // Mark as pending
  prefetchCache.set(cacheKey, { status: 'pending' });
  
  try {
    const params = new URLSearchParams({
      title: title,
      artist: artist,
    });

    const response = await fetch(
      `${API_URL}/api/music/lyrics?${params.toString()}`,
      { headers: { 'Accept': 'application/json' } }
    );

    if (response.ok) {
      const data = await response.json();
      prefetchCache.set(cacheKey, { status: 'success', data });
      console.log(`[LyricsPrefetch] Cached: "${title}" by "${artist}"`);
    } else {
      prefetchCache.set(cacheKey, { status: 'error', error: 'Not found' });
    }
  } catch (err) {
    prefetchCache.set(cacheKey, { status: 'error', error: err.message });
  }
};

/**
 * Get prefetched lyrics from cache
 * @param {string} title - Song title
 * @param {string} artist - Artist name
 * @returns {Object|null} Cached lyrics data or null
 */
export const getPrefetchedLyrics = (title, artist) => {
  if (!title || !artist) return null;
  
  const cacheKey = `${title.toLowerCase()}-${artist.toLowerCase()}`;
  const cached = prefetchCache.get(cacheKey);
  
  if (cached?.status === 'success') {
    return cached.data;
  }
  
  return null;
};

/**
 * Check if lyrics are being prefetched
 * @param {string} title - Song title
 * @param {string} artist - Artist name
 * @returns {boolean}
 */
export const isLyricsPending = (title, artist) => {
  if (!title || !artist) return false;
  
  const cacheKey = `${title.toLowerCase()}-${artist.toLowerCase()}`;
  const cached = prefetchCache.get(cacheKey);
  
  return cached?.status === 'pending';
};

export default { prefetchLyrics, getPrefetchedLyrics, isLyricsPending };
